// src/services/marketplace.ts
import api from "../lib/api";

export type MarketplaceProject = {
  id: string;
  userId: string;
  projectName: string;
  sector: string;
  country: string;
  owner: string;
  organization: string;
  stage: string;
  summary: string;
  keyActivities: Array<{ activity: string }>;
  productDescription?: string;
  advantages?: string;
  links: Array<{ url: string; title: string }>;
  documents: Array<{ url: string; name: string }>;
  nextSteps?: string;
  status: 'pending' | 'approved' | 'rejected' | 'hidden';
  isActive: boolean;
  rejectionReason?: string;
  approvedAt?: string;
  approvedBy?: string;
  viewCount?: number;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    organizationName?: string;
  };
};

export type InvestorRequest = {
  id: string;
  userId: string;
  projectName: string;
  sector: string;
  investor: string;
  organization: string;
  goal: string;
  preferredIndustry?: string;
  location?: string;
  investmentType?: string;
  proposal?: string;
  roi?: string;
  links: Array<{ url: string; title: string }>;
  documents: Array<{ url: string; name: string }>;
  nextSteps?: string;
  status: 'pending' | 'approved' | 'rejected' | 'hidden';
  isActive: boolean;
  rejectionReason?: string;
  approvedAt?: string;
  approvedBy?: string;
  viewCount?: number;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    organizationName?: string;
  };
};

export type ContactRequest = {
  id: string;
  fromUserId: string;
  toUserId: string;
  itemType: 'project' | 'investor_request';
  itemId: string;
  message: string;
  contactInfo: Record<string, any>;
  status: 'pending' | 'read' | 'responded' | 'archived';
  response?: string;
  respondedAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    organizationName?: string;
  };
  recipient?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    organizationName?: string;
  };
};

// ==================== PROJECT APIs ====================

export async function getAllProjects(filters?: {
  sector?: string;
  country?: string;
  stage?: string;
  search?: string;
}): Promise<MarketplaceProject[]> {
  const params = new URLSearchParams();
  if (filters?.sector) params.append('sector', filters.sector);
  if (filters?.country) params.append('country', filters.country);
  if (filters?.stage) params.append('stage', filters.stage);
  if (filters?.search) params.append('search', filters.search);

  const { data } = await api.get(`/api/marketplace/projects?${params.toString()}`);
  return data.data.projects;
}

export async function getProjectById(id: string): Promise<MarketplaceProject> {
  const { data } = await api.get(`/api/marketplace/projects/${id}`);
  return data.data.project;
}

export async function createProject(projectData: Partial<MarketplaceProject>): Promise<MarketplaceProject> {
  const { data } = await api.post('/api/marketplace/projects', projectData);
  return data.data.project;
}

export async function updateProject(id: string, projectData: Partial<MarketplaceProject>): Promise<MarketplaceProject> {
  const { data } = await api.put(`/api/marketplace/projects/${id}`, projectData);
  return data.data.project;
}

export async function deleteProject(id: string): Promise<void> {
  await api.delete(`/api/marketplace/projects/${id}`);
}

export async function getMyProjects(): Promise<MarketplaceProject[]> {
  const { data } = await api.get('/api/marketplace/my-projects');
  return data.data.projects;
}

// ==================== INVESTOR REQUEST APIs ====================

export async function getAllInvestorRequests(filters?: {
  sector?: string;
  search?: string;
}): Promise<InvestorRequest[]> {
  const params = new URLSearchParams();
  if (filters?.sector) params.append('sector', filters.sector);
  if (filters?.search) params.append('search', filters.search);

  const { data } = await api.get(`/api/marketplace/investor-requests?${params.toString()}`);
  return data.data.requests;
}

export async function getInvestorRequestById(id: string): Promise<InvestorRequest> {
  const { data } = await api.get(`/api/marketplace/investor-requests/${id}`);
  return data.data.request;
}

export async function createInvestorRequest(requestData: Partial<InvestorRequest>): Promise<InvestorRequest> {
  const { data } = await api.post('/api/marketplace/investor-requests', requestData);
  return data.data.request;
}

export async function updateInvestorRequest(id: string, requestData: Partial<InvestorRequest>): Promise<InvestorRequest> {
  const { data } = await api.put(`/api/marketplace/investor-requests/${id}`, requestData);
  return data.data.request;
}

export async function deleteInvestorRequest(id: string): Promise<void> {
  await api.delete(`/api/marketplace/investor-requests/${id}`);
}

export async function getMyInvestorRequests(): Promise<InvestorRequest[]> {
  const { data } = await api.get('/api/marketplace/my-investor-requests');
  return data.data.requests;
}

// ==================== CONTACT REQUEST APIs ====================

export async function createContactRequest(contactData: {
  toUserId: string;
  itemType: 'project' | 'investor_request';
  itemId: string;
  message: string;
  contactInfo?: Record<string, any>;
}): Promise<ContactRequest> {
  const { data } = await api.post('/api/marketplace/contact-requests', contactData);
  return data.data.contactRequest;
}

export async function getMyReceivedRequests(): Promise<ContactRequest[]> {
  const { data } = await api.get('/api/marketplace/contact-requests/received');
  return data.data.requests;
}

export async function getMySentRequests(): Promise<ContactRequest[]> {
  const { data } = await api.get('/api/marketplace/contact-requests/sent');
  return data.data.requests;
}

export async function updateContactRequestStatus(
  id: string,
  status: 'pending' | 'read' | 'responded' | 'archived',
  response?: string
): Promise<ContactRequest> {
  const { data } = await api.patch(`/api/marketplace/contact-requests/${id}`, {
    status,
    response
  });
  return data.data.request;
}

export async function startConversationFromContactRequest(id: string): Promise<{ otherUserId: string }> {
  const { data } = await api.post(`/api/marketplace/contact-requests/${id}/start-conversation`);
  return data.data;
}

// ==================== ADMIN APIs ====================

export async function getAllProjectsAdmin(filters?: {
  sector?: string;
  country?: string;
  stage?: string;
  status?: string;
  search?: string;
}): Promise<MarketplaceProject[]> {
  const params = new URLSearchParams();
  if (filters?.sector) params.append('sector', filters.sector);
  if (filters?.country) params.append('country', filters.country);
  if (filters?.stage) params.append('stage', filters.stage);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);

  const { data } = await api.get(`/api/admin/marketplace/projects?${params.toString()}`);
  return data.data.projects;
}

export async function approveProject(id: string): Promise<MarketplaceProject> {
  const { data } = await api.patch(`/api/admin/marketplace/projects/${id}/approve`);
  return data.data.project;
}

export async function rejectProject(id: string, reason?: string): Promise<MarketplaceProject> {
  const { data } = await api.patch(`/api/admin/marketplace/projects/${id}/reject`, { reason });
  return data.data.project;
}

export async function hideProject(id: string): Promise<MarketplaceProject> {
  const { data } = await api.patch(`/api/admin/marketplace/projects/${id}/hide`);
  return data.data.project;
}

export async function deleteProjectAdmin(id: string): Promise<void> {
  await api.delete(`/api/admin/marketplace/projects/${id}`);
}

export async function getAllInvestorRequestsAdmin(filters?: {
  sector?: string;
  status?: string;
  search?: string;
}): Promise<InvestorRequest[]> {
  const params = new URLSearchParams();
  if (filters?.sector) params.append('sector', filters.sector);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);

  const { data } = await api.get(`/api/admin/marketplace/investor-requests?${params.toString()}`);
  return data.data.requests;
}

export async function approveInvestorRequest(id: string): Promise<InvestorRequest> {
  const { data } = await api.patch(`/api/admin/marketplace/investor-requests/${id}/approve`);
  return data.data.request;
}

export async function rejectInvestorRequest(id: string, reason?: string): Promise<InvestorRequest> {
  const { data } = await api.patch(`/api/admin/marketplace/investor-requests/${id}/reject`, { reason });
  return data.data.request;
}

export async function hideInvestorRequest(id: string): Promise<InvestorRequest> {
  const { data } = await api.patch(`/api/admin/marketplace/investor-requests/${id}/hide`);
  return data.data.request;
}

export async function deleteInvestorRequestAdmin(id: string): Promise<void> {
  await api.delete(`/api/admin/marketplace/investor-requests/${id}`);
}

export async function getAllContactRequestsAdmin(filters?: {
  status?: string;
  itemType?: string;
}): Promise<ContactRequest[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.itemType) params.append('itemType', filters.itemType);

  const { data } = await api.get(`/api/admin/marketplace/contact-requests?${params.toString()}`);
  return data.data.requests;
}
